import React, { useState, useEffect } from "react";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";
import HomePage from "./screens/homePage";
import ProductsPage from "./screens/productsPage";
import OrdersPage from "./screens/ordersPage";
import UserPage from "./screens/userPage";
import HomeNavbar from "./components/headers/HomeNavbar";
import OtherNavbar from "./components/headers/OtherNavbar";
import Footer from "./components/footer";
import HelpPage from "./screens/helpPage";
import useBasket from "./hooks/useBasket";
import AuthenticationModal from "./components/auth";
import { sweetErrorHandling, sweetTopSuccessAlert } from "../lib/sweetAlert";
import { Messages } from "../lib/config";
import MemberService from "./services/MemberService";
import { useGlobals } from "./hooks/useGlobals";
import "../css/app.css";
import "../css/navbar.css";
import "../css/footer.css";
import QrLanding from "./components/qrLanding";
import TableService from "./services/TableService";
import CallButton from "./components/callWaiter";

export default function App() {
  const location = useLocation();
  const history = useHistory();
  const { setAuthMember, authMember, authTable, setAuthTable } = useGlobals();
  const { cartItems, onAdd, onRemove, onDelete, onDeleteAll } = useBasket();
  const [signupOpen, setSignupOpen] = useState<boolean>(false);
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Route protection: authTable can only access /products and /orders
  useEffect(() => {
    if (authTable) {
      const restrictedRoutes = ["/", "/member-page", "/help"];
      if (restrictedRoutes.includes(location.pathname)) {
        history.push("/products");
      }
    }
  }, [authTable, location.pathname, history]);

  /** Handlers */
  const handleSignupClose = () => setSignupOpen(false);
  const handleLoginClose = () => setLoginOpen(false);

  const handleLogoutOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseLogout = () => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    setAnchorEl(null);
  };

  const handleLogoutRequest = async () => {
    try {
      const member = new MemberService();
      const table = new TableService();
      authTable ? await table.tableLogout() : await member.logout();

      await sweetTopSuccessAlert("success", 700);
      setAuthTable(null);
      setAuthMember(null);
    } catch (err) {
      console.log(err);
      sweetErrorHandling(Messages.error1);
    }
  };

  const handleLogoutClick = async (e: React.MouseEvent<HTMLLIElement>) => {
    (e.currentTarget as HTMLElement).blur();
    handleCloseLogout();
    await Promise.resolve();
    await handleLogoutRequest();
  };

  const callHandler = async (id: string) => {
    const table = new TableService();
    try {
      if (!authTable) throw new Error(Messages.error6);
      const result = await table.clickTableCall(id);
      sweetTopSuccessAlert("The waiter is coming!", 700);
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  return (
    <>
      {location.pathname === "/" ? (
        <HomeNavbar
          cartItems={cartItems}
          onAdd={onAdd}
          onRemove={onRemove}
          onDelete={onDelete}
          onDeleteAll={onDeleteAll}
          setSignupOpen={setSignupOpen}
          setLoginOpen={setLoginOpen}
          anchorEl={anchorEl}
          handleLogoutOpen={handleLogoutOpen}
          handleCloseLogout={handleCloseLogout}
          handleLogoutClick={handleLogoutClick}
        />
      ) : (
        <OtherNavbar
          cartItems={cartItems}
          onAdd={onAdd}
          onRemove={onRemove}
          onDelete={onDelete}
          onDeleteAll={onDeleteAll}
          setSignupOpen={setSignupOpen}
          setLoginOpen={setLoginOpen}
          anchorEl={anchorEl}
          handleLogoutOpen={handleLogoutOpen}
          handleCloseLogout={handleCloseLogout}
          handleLogoutClick={handleLogoutClick}
        />
      )}
      <Switch>
        <Route path="/products">
          <ProductsPage onAdd={onAdd} />
        </Route>
        <Route path="/orders">
          <OrdersPage callHandler={callHandler} />
        </Route>
        <Route path="/member-page">
          <UserPage />
        </Route>
        <Route path="/help">
          <HelpPage />
        </Route>
        <Route path={"/table/qr/:id"}>
          <QrLanding />
        </Route>
        <Route path="/">
          <HomePage />
        </Route>
      </Switch>
      <Footer />
      {authTable && <CallButton callHandler={callHandler} />}

      <AuthenticationModal
        signupOpen={signupOpen}
        loginOpen={loginOpen}
        handleLoginClose={handleLoginClose}
        handleSignupClose={handleSignupClose}
      />
    </>
  );
}
